/** Shared entity-picker contracts; optional request flags scope behavior to the caller that asks for it. */
import { ContactType } from '../../contact/contact.type';
import { Entity } from '../../entity';
import { PriceListCoverageParameters, PricelistStatus } from '../../price.book';
import { PriceListCategory } from '../../price.list.category';
import { PickerEntity } from './picker.entity';
import { EntityPagingInfo } from '../common/entity.paging.info';
import { ActivityEntityName } from '../../shared/activity.entity.name';
import { LabelType } from '../../shared/label';
import { JobTemplateMode } from '../../enums';
import { EntityViewFilter } from '../views/entity.view.filter';
import { EntityStatusType } from '../../entity.status/enum';
import { UserType } from '../../tenant/user.type';
import { DocumentThemeScopeRequest } from '../../document.themes/document.theme';
import { StockLocationType } from '../../equipment/enums';
import { DocumentMergeType } from '../../shared/document.library';

export type CrmPickerMode = 'Default' | 'SMS' | 'Email';

export interface PickerCrmRequest extends PickerRequest {
    types: ContactType[];
    mode?: CrmPickerMode;
    excludeIds?: string[];
    includeMe?: boolean;
    schedulingEnabled?: boolean;
    /** Assignment fields opt in to existing compliance disabling; ordinary contact pickers are unchanged. */
    checkAssignmentCompliance?: boolean;

    /** @description: For job legacy filter. It can get active:false. */
    includeDeleted?: boolean;
    includeInactive?: boolean;

    /** Financial document party: one row per account, named for the company it bills to (WBS-11914). */
    tradingAs?: boolean;

    /** Employees only: limit to people whose role holds every one of these rights (queue sharing). */
    requiredRights?: number[];

    relatedEntity?: {
        type: ActivityEntityName;
        id: string;
    }
}

export interface PickerEntityContactRequest extends PickerRequest {
    mode: 'SMS' | 'Email' | 'EmailPlaceholder' | 'SMSPlaceholder';
    parentEntity?: ActivityEntityName;
    parentId?: string;
    types?: ContactType[];
}

export interface PickerSiteRequest extends PickerRequest {
    parentType?: ContactType[];
    excludeIds?: string[];

    /** @description for picker that display contact as primary label */
    parentFirst?: boolean;

    skipIfParentDisabled?: boolean
}

export interface PickerPriceListRequest extends PickerRequest {
    includeGlobal: boolean;
    status?: PricelistStatus;
    parentOnly?: boolean;
    category: PriceListCategory;
    includeExpired: boolean;

    sortGroup?: boolean;

    /**
     * Billing companies whose scoped pricebooks belong in the list. A pricebook bound to another
     * company prices against the wrong ledger, so a company-scoped caller must pass this — an
     * absent value lists every company's books, which is how a Sell picker ends up showing Buy.
     * Unscoped (no `company`) books always survive: they are the tenant-wide defaults.
     */
    companyIds?: string[];

    /**
     * Document context used to float matching pricebooks into a `Recommended` group. Purely
     * presentational — a book that fails coverage is still selectable, it just sorts below.
     */
    coverage?: PriceListCoverageParameters;
}

export interface PickerDocumentThemeRequest extends PickerRequest {
    entity: ActivityEntityName;
    scope: DocumentThemeScopeRequest;
}

export interface PickerTagRequest extends PickerRequest {
    /**
     * Primary / legacy single type. Still set when only one type is requested.
     * Prefer `types` when the field may load more than one LabelType (e.g. Task + Phase).
     */
    type?: LabelType;

    /**
     * When set (and non-empty), the picker matches labels of any of these types.
     * Overrides a lone `type` for matching; callers should still pass `type: types[0]` for older clients.
     */
    types?: LabelType[];

    /** Opt in to include code-only system tags (claim/unassigned/etc). Assignment pickers omit this so users can't select system-managed tags; filter surfaces set it true. */
    includeSystemTags?: boolean;
}

export interface PickerSecurityRoleRequest extends PickerRequest {
    types?: UserType[];
}

export interface PickerJobTemplateRequest extends PickerRequest {
    templateMode: JobTemplateMode;
    templateModes: JobTemplateMode[];
    scope?: 'Site' | 'Asset';
    serviceTypeId?: string;
    entity?: ActivityEntityName;
}

export type LegacyJobFilter = 'labels' | 'customers' | 'priceBooks' | 'billingCompanies' | 'workCategories' | 'jobTypes' | 'priorities' | 'zones' | 'owners' | 'fieldSupervisors' | 'resources' | 'statuses' | 'technicians' | 'employees' | 'claimTags' | 'finance' | 'flagCategories';
export interface PickerLegacyJobRequest extends PickerRequest {
    viewId?: string;
    field?: LegacyJobFilter;
    includeInactive?: boolean;
}

export enum PickerLoad {
    Eager = 'Eager',
    Lazy = 'Lazy',
}

export interface PickerRequest {
    search?: string;
    parentId?: string | string[];
    excludeIds?: string[];
    companyIds?: string[];
    page?: number;
    pageSize?: number;
    filters?: EntityViewFilter<any>[];
    addText?: string;
    fetch?: PickerLoad;
    itemTransform?: (item: any) => any;

    /** @description return only the display binding metadata (hasAvatar, bind* fields) without loading items */
    metadataOnly?: boolean;

    /**
     * @description section the results by the requested parent each item belongs to, for a picker
     * asking across more than one `parentId`. The strategy decides the group labels and answers
     * with `bindGroup`; without it the items carry no trace of which parent matched.
     */
    groupByParent?: boolean;

    /**
     * @description with `groupByParent`: the heading for each requested parent. A row joins the first
     * parent it belongs to in `parentId` order, and groups follow that order. Absent → the strategy's own labels.
     */
    parentLabels?: Record<string, string>;

    [key: string]: any;
    entityIds?: Record<ActivityEntityName, string | string[]>;

    variant?: string;
}

export interface PickerAutoCompleteRequest extends PickerRequest {
    // This the field in the document that will be turned into an autocomplete list
    fieldId: string;
}

export interface AutoSelectOptions {
    autoSelectCurrentUser?: boolean;
    autoSelectSingleCompany?: boolean;
    autoSelectFirst?: boolean;
    autoSelectDefault?: boolean;
}

export interface PickerEntityStatusRequest extends PickerRequest {
    /** One entity, or several to offer their sets merged — see TenantEntityStatusEntityDataStrategy.getPicker. */
    entityType: ActivityEntityName | ActivityEntityName[];
    excludeIds?: string[];
    displayType?: EntityStatusType;

    /**
     * Set by a control that moves a record into the chosen status, so `selectableBy` narrows the list.
     *
     * A filter leaves it unset and gets every status: the filter and the picker share this endpoint, and
     * hiding a status from a filter hides the records sitting in it.
     */
    restrictToSelectable?: boolean;
}

export interface PickerStockLocationRequest extends PickerRequest {
    type?: StockLocationType | StockLocationType[];
}

export interface PickerFileTemplateRequest extends PickerRequest {
    mergeTypes?: DocumentMergeType[];
}
export interface ViewFilterRequest {
    viewId: string;
    fieldId: string;
    parentId?: string;
    search?: string;

    page?: number;
    pageSize?: number;
    filters?: EntityViewFilter[];
    [key: string]: any;
}

export interface PickerResponse<T extends Entity = PickerEntity> extends EntityPagingInfo {
    items: T[];
    hasColor?: boolean;
    hasAvatar?: boolean;
    bindName?: string;
    bindDescription1?: string;
    bindDescription2?: string;
    bindDescription3?: string;
    bindImage?: string;
    bindTags?: string;
    bindGroup?: string;
}

export interface PickerJobPolicyRequest extends PickerRequest {
    jobTypes?: string[];
    /** Companies whose scoped policies should be included alongside the unscoped ones. */
    companyIds?: string[];
    /** List every policy regardless of company/job-type scoping — bulk pickers are selection-independent and defer applicability to per-row validation (WBS-10304). */
    anyScope?: boolean;
}

export interface PickerQuotePolicyRequest extends PickerJobPolicyRequest {
}

export function emptyPickerResult<T extends Entity = PickerEntity>(): PickerResponse<T> {
    return {
        page: 0,
        pageSize: 0,
        total: 0,
        items: []
    }
}
